import { Download, Share, X } from "lucide-react";
import { useEffect, useState } from "react";
import { isAgentWorkspace } from "../lib/registerServiceWorker";

interface InstallEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}
let deferredInstallEvent: InstallEvent | null = null;
let deferredStaffWorkspace = false;
window.addEventListener("beforeinstallprompt", (browserEvent) => {
  browserEvent.preventDefault();
  deferredInstallEvent = browserEvent as InstallEvent;
  deferredStaffWorkspace = isAgentWorkspace();
  window.dispatchEvent(new Event("support-install-available"));
});
window.addEventListener("appinstalled", () => {
  deferredInstallEvent = null;
  window.dispatchEvent(new Event("support-install-complete"));
});

function isIos() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
}

export function InstallPrompt({ compact = false }: { compact?: boolean }) {
  const staff = isAgentWorkspace();
  const dismissKey = staff ? "agent-install-dismissed" : "support-install-dismissed-v2";
  const [event, setEvent] = useState<InstallEvent | null>(deferredStaffWorkspace === staff ? deferredInstallEvent : null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(dismissKey) === "1");
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    const beforeInstall = () => setEvent(deferredStaffWorkspace === staff ? deferredInstallEvent : null);
    const appInstalled = () => {
      sessionStorage.setItem(dismissKey, "1");
      setEvent(null);
      setDismissed(true);
    };
    window.addEventListener("support-install-available", beforeInstall);
    window.addEventListener("support-install-complete", appInstalled);
    window.addEventListener("appinstalled", appInstalled);
    return () => {
      window.removeEventListener("support-install-available", beforeInstall);
      window.removeEventListener("support-install-complete", appInstalled);
      window.removeEventListener("appinstalled", appInstalled);
    };
  }, [dismissKey]);

  if ((!compact && dismissed) || isStandalone() || (!event && !isIos() && !compact)) return null;

  async function install() {
    if (event) {
      try {
        await event.prompt();
        const choice = await event.userChoice;
        if (choice.outcome === "accepted") setDismissed(true);
      } catch { setShowIosHelp(true); }
      finally { deferredInstallEvent = null; setEvent(null); }
      return;
    }
    setShowIosHelp(true);
  }

  if (compact) {
    return <div className="install-prompt--compact">
      {!showIosHelp && <button type="button" className="button button--secondary button--small" onClick={install}>
        <Download aria-hidden="true" size={16} /> Add web app
      </button>}
      {showIosHelp && <p role="status">{isIos()
        ? <><Share aria-hidden="true" size={15} /> Open this workspace in Safari, tap Share, then choose <strong>Add to Home Screen</strong>.</>
        : <>Use your browser menu to install this web app. If no install option appears, reload this workspace or use a supported browser.</>}</p>}
    </div>;
  }

  return (
    <aside className="install-prompt" aria-label="Add the Next-Cart Support web app to this device">
      <span className="install-prompt__icon"><Download aria-hidden="true" size={21} /></span>
      <div>
        <strong>Add {staff ? "Agent workspace" : "Support"} to this device</strong>
        <p>{showIosHelp ? <>Tap <Share aria-hidden="true" size={14} /> Share, then “Add to Home Screen”.</> : "Save this support portal for quicker access. No App Store or Google Play download is required."}</p>
      </div>
      {!showIosHelp && <button className="button button--primary button--small" type="button" onClick={install}>Add to device</button>}
      <button
        className="icon-button icon-button--compact"
        type="button"
        aria-label="Dismiss install suggestion"
        onClick={() => {
          sessionStorage.setItem(dismissKey, "1");
          setDismissed(true);
        }}
      >
        <X aria-hidden="true" size={16} />
      </button>
    </aside>
  );
}
